import type {SsoBannerResponse} from '@github-ui/global-sso-banner-ui/types'
import {Type} from '@github-ui/react-query-persister/typebox'
import type {PickerRepository} from '@github-ui/repos-picker'
import type {SidebarLink} from '@github-ui/saved-views'
import {
  PullItemSchema,
  type DeferredItem,
  type EnrichedPull,
  type PullItem,
} from '@github-ui/shared-dashboard-lists/pulls/types/list-types'
import type {PageInfo, StateCountPageInfo} from '@github-ui/shared-dashboard-lists/shared/types/list-types'

export interface PullsDashboardSurfacePayload {
  results: PullItem[]
  pageInfo: PageInfo
  error?: string | null
}

export interface PullsDashboardStateCountSurfacePayload extends PullsDashboardSurfacePayload {
  pageInfo: StateCountPageInfo
}

export const PullsInboxMaxAge = {
  OneMonth: '1m',
  ThreeMonths: '3m',
  OneYear: '1y',
  None: 'none',
} as const

export type PullsInboxMaxAge = (typeof PullsInboxMaxAge)[keyof typeof PullsInboxMaxAge]

export const PULLS_INBOX_MAX_AGE_LABELS: Record<PullsInboxMaxAge, string> = {
  [PullsInboxMaxAge.OneMonth]: 'Last month',
  [PullsInboxMaxAge.ThreeMonths]: 'Last 3 months',
  [PullsInboxMaxAge.OneYear]: 'Last year',
  [PullsInboxMaxAge.None]: 'Any time',
}

export interface InboxFilters {
  maxPrAge?: PullsInboxMaxAge
}

export interface PullsDashboardLayoutPayload {
  savedViews: SidebarLink[]
  sidebarLinks: SidebarLink[]
  sidebarExpanded?: boolean
  compactMode?: boolean
  defaultViewId?: string
  inboxSectionQueries?: Record<string, string>
  filters?: InboxFilters
  selectedRepos?: PickerRepository[]
  sso?: SsoBannerResponse
  collapsedSections?: string[]
  sectionOrder?: string[]
  hiddenSections?: string[]
  inboxOptionsTipDismissed?: boolean
}

export interface PullsDashboardDeferredDataPayload {
  payload: {
    pullsDashboardSurfaceContentDeferredData: {
      results: DeferredItem[]
    }
  }
}

export interface PullsInboxDeferredDataPayload {
  payload: {
    pullsInboxSurfaceContentDeferredData: {
      results: DeferredItem[]
    }
  }
}

export const SectionDataResultSchema = Type.Object({
  results: Type.Array(PullItemSchema),
  totalCount: Type.Number(),
})
export type SectionDataResult = Type.Static<typeof SectionDataResultSchema>

export interface PullsDashboardEnrichedDataPayload {
  pulls: EnrichedPull[]
  pageInfo: StateCountPageInfo
  isFetching?: boolean
  isPending?: boolean
  failedRefetch?: {
    query: string
    page: number
    updateCount: number
  }
  retryFailedRefetch: () => Promise<{isRefetchError: boolean}>
  error?: string | null
}
